import api from '@/services/api';
import type {
    EffectiveSecretSettingState,
    SecretSettingState,
    SecretSettingStatus,
} from '../types/dealerSecretSetting.types';

const MALFORMED = 'Malformed secret setting response';

const secretSettingPath = (dealerId: string, key: string): string =>
    `/dealers/${dealerId}/secret-config-settings/${key}`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

const readLast4 = (value: unknown): string | null => {
    if (value === null) return null;
    if (typeof value === 'string') return value;
    throw new Error(MALFORMED);
};

const readNullableString = (value: unknown): string | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') return value;
    throw new Error(MALFORMED);
};

const readHasValue = (value: unknown): boolean => {
    if (typeof value === 'boolean') return value;
    throw new Error(MALFORMED);
};

const readOwn = (raw: unknown): SecretSettingState => {
    if (!isRecord(raw)) throw new Error(MALFORMED);
    return {
        has_value: readHasValue(raw.has_value),
        last4: readLast4(raw.last4),
        updated_at: readNullableString(raw.updated_at),
    };
};

const readEffective = (raw: unknown): EffectiveSecretSettingState => {
    if (!isRecord(raw)) throw new Error(MALFORMED);
    return {
        has_value: readHasValue(raw.has_value),
        last4: readLast4(raw.last4),
        inherited: typeof raw.inherited === 'boolean' ? raw.inherited : null,
        source_dealer_id: readNullableString(raw.source_dealer_id),
        source_dealer_name: readNullableString(raw.source_dealer_name),
    };
};

const readStatus = (body: unknown): SecretSettingStatus => {
    const data = isRecord(body) ? body.data : undefined;
    if (!isRecord(data) || typeof data.key !== 'string') throw new Error(MALFORMED);
    return { key: data.key, own: readOwn(data.own), effective: readEffective(data.effective) };
};

export const fetchSecretSettingStatus = async (dealerId: string, key: string): Promise<SecretSettingStatus> =>
    readStatus((await api.get<unknown>(secretSettingPath(dealerId, key))).data);

export const putSecretSetting = async (
    dealerId: string,
    key: string,
    secret: string,
): Promise<SecretSettingStatus> =>
    readStatus((await api.put<unknown>(secretSettingPath(dealerId, key), { secret })).data);

export const removeSecretSetting = async (dealerId: string, key: string): Promise<SecretSettingStatus> =>
    readStatus((await api.delete<unknown>(secretSettingPath(dealerId, key))).data);
